/**
 * Vendor Import — Review step row.
 *
 * Renders a single proposed property mapping with a per-row override between
 * mapping to an existing property, creating a new one, or skipping it.
 * Extracted from MappingReviewStep so each row is memoized independently of
 * the surrounding table.
 */

import type { Key } from 'react';
import React, { useCallback } from 'react';

import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { Table } from '@/lib/base/table/table';
import { cx } from '@/utils/cx';

import type { MappingAction, MappingProposal } from './types';

function describeMapping(
  proposal: MappingProposal,
  hasExistingTarget: boolean,
  isSkipped: boolean
): string {
  if (hasExistingTarget) {
    return `Matches your existing "${proposal.target_property_name}" property`;
  }
  // Conditional tense when the row is skipped: "we'll create X" next to an Action
  // of "Skip" reads as a contradiction — nothing is going to be created. This
  // describes what the row would do if the user turns it back on.
  return isSkipped
    ? `No matching property yet – would create "${proposal.label}"`
    : `No matching property yet – we'll create "${proposal.label}"`;
}

/**
 * "3 of 285 have this set", or "Nothing to import" at zero.
 *
 * Returns null when the backend didn't send a count (an older response), so an
 * absent count renders nothing rather than a misleading "0 of 0".
 */
function describeCoverage(
  proposal: MappingProposal,
  totalRecords: number | undefined
): string | null {
  if (proposal.populated_count === undefined || totalRecords === undefined) {
    return null;
  }
  if (proposal.populated_count === 0) {
    return 'Nothing to import';
  }
  return `${proposal.populated_count} of ${totalRecords} have this set`;
}

function describeAction(
  action: MappingAction,
  proposal: MappingProposal
): string {
  switch (action) {
    case 'map_existing':
      return `Map to ${proposal.target_property_name}`;
    case 'create_new':
      return 'Create new property';
    case 'skip':
      return 'Skip';
  }
}

interface ReviewRowProps {
  rowKey: string;
  proposal: MappingProposal;
  totalRecords: number | undefined;
  selectedAction: MappingAction;
  onActionChange: (key: string, action: MappingAction) => void;
}

function ReviewRowComponent({
  rowKey,
  proposal,
  totalRecords,
  selectedAction,
  onActionChange,
}: ReviewRowProps) {
  const coverage = describeCoverage(proposal, totalRecords);
  const isEmpty = proposal.populated_count === 0;

  // Map-to-existing is only offered when the engine actually matched an existing
  // property. `matched_existing` is the engine's own finding — do NOT re-derive
  // it from proposed_action (a zero-count row is `skip` either way) or from
  // target_property_name (which holds a name we'd MINT on the create branch).
  // Inferring it claimed a match on every skipped row against an org that had no
  // custom properties at all, and offered a "Map to org_x" that pointed nowhere.
  //
  // Falls back to the action for a response predating the field, which is correct
  // for every non-skipped row and conservative for the rest.
  const hasExistingTarget =
    proposal.matched_existing ?? proposal.proposed_action === 'map_existing';
  const availableActions: MappingAction[] = hasExistingTarget
    ? ['map_existing', 'create_new', 'skip']
    : ['create_new', 'skip'];

  const actionItems = availableActions.map(action => ({
    id: action,
    label: describeAction(action, proposal),
  }));

  const handleActionChange = useCallback(
    (key: Key | null) => {
      if (key == null) {
        return;
      }
      onActionChange(rowKey, key as MappingAction);
    },
    [rowKey, onActionChange]
  );

  return (
    <Table.Row id={rowKey}>
      <Table.Cell>
        <span className='text-sm font-medium text-primary'>
          {proposal.label}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span className='text-sm text-tertiary'>
          {describeMapping(
            proposal,
            hasExistingTarget,
            selectedAction === 'skip'
          )}
        </span>
      </Table.Cell>
      <Table.Cell>
        <span
          className={cx(
            'text-sm',
            isEmpty ? 'text-quaternary' : 'text-tertiary'
          )}
        >
          {coverage ?? '—'}
        </span>
      </Table.Cell>
      <Table.Cell>
        <Select
          aria-label={`Action for ${proposal.label}`}
          size='xs'
          value={selectedAction}
          onChange={handleActionChange}
          items={actionItems}
        >
          {item => <SelectItem {...item} />}
        </Select>
      </Table.Cell>
    </Table.Row>
  );
}

export const ReviewRow = React.memo(ReviewRowComponent);
